import Framework from '@greenville/framework';
import { applySnapshot, types } from 'mobx-state-tree';
import { toJS } from 'mobx';
import * as constants from '../../../common/constants';
import utils from '../../../common/utils';
import { buildRowScopedExportPayload } from '../common/utils/auditExportColumnUtils';

const chartsListData = types.model('chartsListData', {
  type: types.maybeNull(types.string),
  feedback: types.maybeNull(types.string),
  identifierId: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
  feedbackId: types.maybeNull(types.string),
  content: types.maybeNull(types.string),
  userId: types.maybeNull(types.string),
  comments: types.maybeNull(types.string),
  reasonForDislike: types.maybeNull(types.string),
  serverDateTime: types.maybeNull(types.string),
  createdDate: types.maybeNull(types.number),
  requestId: types.maybeNull(types.string),
  fromAgentic: types.maybeNull(types.boolean)
});

const TrendsUserFeedbackDetailsModel = types
  .model('TrendsUserFeedbackDetailsModel', {
    status: types.maybeNull(types.string),
    data: types.optional(types.array(chartsListData), []),
    totalCount: types.optional(types.number, 0),
    totalPages: types.optional(types.number, 0),
    currentPage: types.optional(types.number, 0),
    pageSize: types.optional(types.number, constants.PAGINATION.LIMIT),
    lastPayload: types.optional(types.frozen(), {})
  })
  .views((self) => ({
    get feedbackData() {
      return toJS(self.data);
    },
    get hasData() {
      return self.data.length > 0;
    },
    get paginationInfo() {
      return {
        totalCount: self.totalCount,
        totalPages: self.totalPages,
        currentPage: self.currentPage,
        pageSize: self.pageSize
      };
    },
    /** Export filters; start/end dates come from loaded row timestamps, not search date pickers. */
    get exportPayload() {
      return buildRowScopedExportPayload(self.lastPayload, self.data);
    }
  }))
  .actions((self) => ({
    setPageSize(size) {
      applySnapshot(self, { ...self, pageSize: size });
    },
    setLastPayload(payload) {
      applySnapshot(self, { ...self, lastPayload: payload });
    },
    setTotalCount(count) {
      applySnapshot(self, { ...self, totalCount: count });
    },
    setTotalPages(pages) {
      applySnapshot(self, { ...self, totalPages: pages });
    },
    setCurrentPage(page) {
      applySnapshot(self, { ...self, currentPage: page });
    },
    fetchInitial(payload = {}) {
      self.resetData();

      const requestPayload = {
        ...payload,
        offset: 0,
        limit: self.pageSize
      };

      self.setLastPayload(requestPayload);

      Framework.getEventManager().publish(constants.GET_AI_CHARTS_UTILITY_LIST_DATA, {
        payload: requestPayload
      });
    },
    fetchPage(page, limit = null) {
      const pageSize = limit || self.pageSize;
      const offset = page * pageSize;

      const requestPayload = {
        ...self.lastPayload,
        offset,
        limit: pageSize
      };

      self.setPageSize(pageSize);
      self.setLastPayload(requestPayload);

      Framework.getEventManager().publish(constants.GET_AI_CHARTS_UTILITY_LIST_DATA, {
        payload: requestPayload
      });
    },
    fetch(payload) {
      if (payload?.isInitialState) {
        const initialState = [];
        applySnapshot(self.data, initialState);
        Framework.getEventManager().publish(constants.SET_AI_CHARTS_UTILITY_LIST_DATA);
      }
      Framework.getEventManager().publish(constants.GET_AI_CHARTS_UTILITY_LIST_DATA, {
        payload
      });
    },
    setResponse(response) {
      if (!response) {
        Framework.getEventManager().publish(constants.SET_AI_CHARTS_UTILITY_LIST_DATA);
        return;
      }
      let responseData = [];
      let paginationData = null;

      if (response.status === 'success' && Array.isArray(response.data)) {
        responseData = response.data;
        paginationData = response.pagination || null;
      } else if (response.success !== undefined) {
        responseData = response.data || [];
        paginationData = response.pagination || null;
      } else if (Array.isArray(response)) {
        responseData = response;
      } else if (response.data) {
        responseData = Array.isArray(response.data.data) ? response.data.data : response.data;
        paginationData = response.pagination || null;
      }

      const enrichedData = responseData.map((item) => ({
        ...item,
        createdDate: utils.toEpochUTC(item.serverDateTime)
      }));

      applySnapshot(self.data, enrichedData);

      if (paginationData) {
        self.setTotalCount(paginationData.totalCount || 0);
        self.setTotalPages(paginationData.totalPages || 0);

        const currentPageIndex =
          paginationData.offset !== undefined && paginationData.limit
            ? Math.floor(paginationData.offset / paginationData.limit)
            : paginationData.currentPage || 0;
        self.setCurrentPage(currentPageIndex);
      } else {
        self.setTotalCount(responseData.length);
        self.setTotalPages(1);
        self.setCurrentPage(0);
      }
      Framework.getEventManager().publish(constants.SET_AI_CHARTS_UTILITY_LIST_DATA);
    },
    resetData() {
      applySnapshot(self.data, []);
      self.setTotalCount(0);
      self.setTotalPages(0);
      self.setCurrentPage(0);
      self.setLastPayload({});
    },
    resetStoreValues() {
      self.resetData();
      Framework.getEventManager().publish(constants.SET_AI_CHARTS_UTILITY_LIST_DATA);
    }
  }));

export default TrendsUserFeedbackDetailsModel;
